import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  Switch,
  FormControlLabel,
  Tab,
  Tabs,
  Alert,
} from "@mui/material";
import {
  MonetizationOnOutlined,
  ReceiptLongOutlined,
  TrendingUpOutlined,
  DownloadOutlined,
  RefreshOutlined,
  SearchOutlined,
  FilterListOutlined,
  AccountBalanceOutlined,
  PaymentsOutlined,
  CreditCardOutlined,
  CalendarTodayOutlined,
  ClearOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { formatKES } from "../WalletManagement/WalletBalance";
import { companyManagementUrl, walletGraphqlUrl } from "../../config";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import {
  WALLET_QUERY,
  WALLET_LEDGER_QUERY,
  ACTIVE_FEE_SCHEDULE_QUERY,
  FEE_TIERS_QUERY,
  FEE_SCHEDULES_QUERY,
  ACTIVE_WITHDRAWAL_FEE_SCHEDULE_QUERY,
  WITHDRAWAL_FEE_TIERS_QUERY,
  WITHDRAWAL_FEE_SCHEDULES_QUERY,
  createWalletClient,
} from "../../data/WalletService/walletGraphQL";

// â”€â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const toDateTimeStr = (d) =>
  d ? new Date(d).toLocaleString("en-KE", { dateStyle: "short", timeStyle: "short" }) : "â€”";

const FEE_TYPES = {
  DISBURSEMENT_FEE: { label: "Disbursement Fee", color: "#1766a2" },
  WITHDRAWAL_FEE: { label: "Withdrawal Fee", color: "#22bf72" },
  ROYALTY: { label: "Royalty", color: "#ed6c02" },
  OVERDRAFT_INTEREST_REPAY: { label: "Overdraft Interest Repay", color: "#d32f2f" },
};

function classifyTxType(txType = "", description = "") {
  const tx = txType.toUpperCase(), desc = description.toUpperCase();
  if (tx.includes("ROYALTY") || desc.includes("ROYALTY")) return "ROYALTY";
  if (tx.includes("OVERDRAFT_INTEREST_REPAY") || tx === "OVERDRAFT_INTEREST_REPAY" || desc.includes("OVERDRAFT INTEREST REPAY")) return "OVERDRAFT_INTEREST_REPAY";
  if ((tx.includes("WITHDRAWAL") && tx.includes("FEE")) ||
    desc.includes("SABABISHA WITHDRAWAL") || desc.includes("WITHDRAWAL FEE")) return "WITHDRAWAL_FEE";
  if (tx.includes("FEE") || tx.includes("CHARGE") || tx.includes("ACTIVATION") || tx.includes("EXIT"))
    return "DISBURSEMENT_FEE";
  if (tx.includes("DISBURSEMENT") || tx.includes("ESCROW_COMMIT")) return "DISBURSEMENT";
  if (tx.includes("WITHDRAWAL")) return "WITHDRAWAL";
  return null;
}

const RevenueReport = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [activeTab, setActiveTab] = useState(0);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryPage, setSummaryPage] = useState(0);
  const [summaryRowsPerPage, setSummaryRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerPage, setLedgerPage] = useState(0);
  const [ledgerRowsPerPage, setLedgerRowsPerPage] = useState(25);
  const [ledgerSearch, setLedgerSearch] = useState("");
  const [ledgerFeeType, setLedgerFeeType] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [companyList, setCompanyList] = useState([]);
  const [ledgerCompany, setLedgerCompany] = useState("ALL");
  const [ledgerError, setLedgerError] = useState(null);
  const [activeWithdrawalTiers, setActiveWithdrawalTiers] = useState([]);

  const getSababishaWithdrawalFee = useCallback((amount) => {
    if (!amount || amount <= 0) return 0;
    const tier = activeWithdrawalTiers.find((t) => amount >= Number(t.amountFrom ?? 0) && amount <= Number(t.amountTo ?? Infinity));
    return tier ? Number(tier.sababishaCharge ?? 0) : 0;
  }, [activeWithdrawalTiers]);

  useEffect(() => {
    (async () => {
      try {
        const compRes = await fetchJsonDynamic("companies", null, null, companyManagementUrl.uri);
        let raw = [];
        if (compRes?.results) raw = JSON.parse(compRes.results) || [];
        else if (Array.isArray(compRes)) raw = compRes;
        setCompanyList(raw.filter((c) => c?.IsActive || c?.isActive).sort((a, b) => (b?.Id || 0) - (a?.Id || 0)));
        const wc = createWalletClient();
        try {
          const s = await wc.query({ query: ACTIVE_WITHDRAWAL_FEE_SCHEDULE_QUERY, fetchPolicy: "network-only" });
          let sid = s?.data?.activeWithdrawalFeeSchedule?.id;
          if (!sid) { const all = await wc.query({ query: WITHDRAWAL_FEE_SCHEDULES_QUERY, fetchPolicy: "network-only" }); sid = all?.data?.withdrawalFeeSchedules?.[0]?.id; }
          if (sid) { const t = await wc.query({ query: WITHDRAWAL_FEE_TIERS_QUERY, variables: { scheduleId: sid }, fetchPolicy: "network-only" }); setActiveWithdrawalTiers(t?.data?.withdrawalFeeTiers || []); }
        } catch (e) { console.warn("Fee tiers:", e?.message); }
      } catch (e) { console.warn("Bootstrap:", e?.message); }
    })();
  }, []);

  const fetchRevenue = useCallback(async () => {
    setLoading(true);
    try {
      const compRes = await fetchJsonDynamic("companies", null, null, companyManagementUrl.uri);
      let raw = [];
      if (compRes?.results) raw = JSON.parse(compRes.results) || [];
      else if (Array.isArray(compRes)) raw = compRes;
      const companies = raw.filter((c) => c?.IsActive || c?.isActive).sort((a, b) => (b?.Id || b?.id || 0) - (a?.Id || a?.id || 0));
      if (!companies.length) { setRevenueData([]); return; }
      const walletClient = createWalletClient();
      let allWithdrawals = [];
      try {
        const wRes = await fetchJsonDynamic("walletWithdrawal", "wallet", null, walletGraphqlUrl.uri);
        if (wRes?.results) allWithdrawals = JSON.parse(wRes.results) || [];
        else if (Array.isArray(wRes)) allWithdrawals = wRes;
      } catch (e) { console.warn("walletWithdrawal:", e?.message); }
      const walletResults = [], ledgerResults = [];
      for (let i = 0; i < companies.length; i += 8) {
        const chunk = companies.slice(i, i + 8);
        const [wc2, lc] = await Promise.all([
          Promise.allSettled(chunk.map((c) => walletClient.query({ query: WALLET_QUERY, variables: { companyId: c.Id ?? c.id }, fetchPolicy: "network-only" }))),
          Promise.allSettled(chunk.map((c) => walletClient.query({ query: WALLET_LEDGER_QUERY, variables: { companyId: c.Id ?? c.id, page: 1, pageSize: 500 }, fetchPolicy: "network-only" }))),
        ]);
        walletResults.push(...wc2); ledgerResults.push(...lc);
      }
      const mapped = companies.map((c, i) => {
        const cid = c.Id ?? c.id;
        const name = c.CompanyName ?? c.companyName ?? c.Name ?? c.name ?? ("Tenant #" + String(cid).padStart(4, "0"));
        const w = walletResults[i]?.status === "fulfilled" ? walletResults[i].value?.data?.wallet : null;
        const ledger = ledgerResults[i]?.status === "fulfilled" ? ledgerResults[i].value?.data?.walletLedger || [] : [];
        const tenantW = allWithdrawals.filter((tw) => Number(tw.company_id ?? tw.companyId) === Number(cid) && !(tw.iscancelled || tw.isCancelled || tw.is_cancelled));
        let dbWithdrawalFees = 0;
        tenantW.forEach((tw) => {
          const ok = tw.isapproved === 1 || tw.isApproved === 1 || tw.status === "APPROVED" || tw.status === "COMPLETED";
          if (ok) { const f = Number(tw.fee ?? 0); dbWithdrawalFees += f > 0 ? f : getSababishaWithdrawalFee(Number(tw.amount ?? 0)); }
        });
        let totalDisbursed = 0, disbursementCount = 0, disbursementFees = 0, ledgerWithdrawalFees = 0, totalRoyalties = 0;
        let principalWithdrawalFeesEst = 0, overdraftInterestRepay = 0;
        ledger.forEach((e) => {
          const amt = Math.abs(Number(e.amount || 0));
          switch (classifyTxType(e.txType, e.description)) {
            case "DISBURSEMENT": totalDisbursed += amt; disbursementCount++; break;
            case "WITHDRAWAL": totalDisbursed += amt; principalWithdrawalFeesEst += getSababishaWithdrawalFee(amt); break;
            case "ROYALTY": totalRoyalties += amt; break;
            case "WITHDRAWAL_FEE": ledgerWithdrawalFees += amt; break;
            case "DISBURSEMENT_FEE": disbursementFees += amt; break;
            case "OVERDRAFT_INTEREST_REPAY": overdraftInterestRepay += amt; break;
          }
        });
        // one source only: actual ledger fees, else DB, else tier estimate
        const withdrawalFees = ledgerWithdrawalFees || dbWithdrawalFees || principalWithdrawalFeesEst;
        return { companyId: cid, companyName: name, totalDisbursed, disbursementCount, disbursementFees, withdrawalFees, totalRoyalties, overdraftInterestRepay, totalRevenue: disbursementFees + withdrawalFees + totalRoyalties + overdraftInterestRepay, availableBalance: w?.availableBalance ?? 0 };
      });
      setRevenueData(mapped);
    } catch (err) { console.warn("Failed to fetch revenue:", err.message); setRevenueData([]); }
    finally { setLoading(false); }
  }, [getSababishaWithdrawalFee]);

  useEffect(() => { fetchRevenue(); }, [fetchRevenue]);

  const fetchLedger = useCallback(async () => {
    setLedgerLoading(true); setLedgerError(null);
    try {
      const wc = createWalletClient();
      const compRes = await fetchJsonDynamic("companies", null, null, companyManagementUrl.uri);
      let raw = [];
      if (compRes?.results) raw = JSON.parse(compRes.results) || [];
      else if (Array.isArray(compRes)) raw = compRes;
      const companies = raw.filter((c) => c?.IsActive || c?.isActive).sort((a, b) => (b?.Id || 0) - (a?.Id || 0));
      const all = [];
      for (let i = 0; i < companies.length; i += 8) {
        const chunk = companies.slice(i, i + 8);
        const res = await Promise.allSettled(chunk.map((c) => wc.query({ query: WALLET_LEDGER_QUERY, variables: { companyId: c.Id ?? c.id, page: 1, pageSize: 500 }, fetchPolicy: "network-only" })));
        res.forEach((r, idx) => {
          if (r.status === "fulfilled") {
            const cid = chunk[idx].Id ?? chunk[idx].id;
            const cname = chunk[idx].CompanyName ?? chunk[idx].companyName ?? chunk[idx].Name ?? ("Tenant #" + String(cid).padStart(4, "0"));
            (r.value?.data?.walletLedger || []).forEach((e) => { const cat = classifyTxType(e.txType, e.description); if (cat) all.push({ ...e, companyId: cid, companyName: cname, feeCategory: cat }); });
          }
        });
      }
      all.sort((a, b) => new Date(b.createdDate || 0) - new Date(a.createdDate || 0));
      setLedgerEntries(all);
    } catch { setLedgerError("Could not load fee ledger. Please try again."); }
    finally { setLedgerLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === 1 && ledgerEntries.length === 0 && !ledgerLoading) fetchLedger(); }, [activeTab, ledgerEntries.length, ledgerLoading, fetchLedger]);

  const totalDisbursementFees = revenueData.reduce((s, r) => s + r.disbursementFees, 0);
  const totalWithdrawalFees = revenueData.reduce((s, r) => s + r.withdrawalFees, 0);
  const totalRoyalties = revenueData.reduce((s, r) => s + r.totalRoyalties, 0);
  const totalOverdraftInterestRepay = revenueData.reduce((s, r) => s + (r.overdraftInterestRepay || 0), 0);
  const grandTotal = totalDisbursementFees + totalWithdrawalFees + totalRoyalties + totalOverdraftInterestRepay;
  const activeDisbursersCount = revenueData.filter((r) => r.totalRevenue > 0).length;

  const filteredSummary = useMemo(() => revenueData.filter((r) => {
    if (searchQuery.trim()) { const q = searchQuery.toLowerCase(); return (r.companyName || "").toLowerCase().includes(q) || String(r.companyId || "").includes(q); }
    if (activeOnly) return r.totalDisbursed > 0 || r.totalRevenue > 0;
    return true;
  }), [revenueData, searchQuery, activeOnly]);

  const paginatedSummary = useMemo(() => filteredSummary.slice(summaryPage * summaryRowsPerPage, summaryPage * summaryRowsPerPage + summaryRowsPerPage), [filteredSummary, summaryPage, summaryRowsPerPage]);

  const filteredLedger = useMemo(() => ledgerEntries.filter((e) => {
    if (ledgerFeeType !== "ALL" && e.feeCategory !== ledgerFeeType) return false;
    if (ledgerCompany !== "ALL" && String(e.companyId) !== String(ledgerCompany)) return false;
    if (dateFrom || dateTo) {
      const d = e.createdDate ? new Date(e.createdDate) : null;
      if (!d) return false;
      if (dateFrom && d < new Date(dateFrom)) return false;
      if (dateTo) { const t = new Date(dateTo); t.setHours(23, 59, 59, 999); if (d > t) return false; }
    }
    if (ledgerSearch.trim()) { const q = ledgerSearch.toLowerCase(); return (e.companyName || "").toLowerCase().includes(q) || (e.description || "").toLowerCase().includes(q) || (e.txType || "").toLowerCase().includes(q); }
    return true;
  }), [ledgerEntries, ledgerFeeType, ledgerCompany, dateFrom, dateTo, ledgerSearch]);

  const paginatedLedger = useMemo(() => filteredLedger.slice(ledgerPage * ledgerRowsPerPage, ledgerPage * ledgerRowsPerPage + ledgerRowsPerPage), [filteredLedger, ledgerPage, ledgerRowsPerPage]);

  const lTotalDisb = filteredLedger.filter((e) => e.feeCategory === "DISBURSEMENT_FEE").reduce((s, e) => s + Math.abs(Number(e.amount)), 0);
  const lTotalWith = filteredLedger.filter((e) => e.feeCategory === "WITHDRAWAL_FEE").reduce((s, e) => s + Math.abs(Number(e.amount)), 0);
  const lTotalRoy = filteredLedger.filter((e) => e.feeCategory === "ROYALTY").reduce((s, e) => s + Math.abs(Number(e.amount)), 0);
  const lTotalOIR = filteredLedger.filter((e) => e.feeCategory === "OVERDRAFT_INTEREST_REPAY").reduce((s, e) => s + Math.abs(Number(e.amount)), 0);
  const lGrand = lTotalDisb + lTotalWith + lTotalRoy + lTotalOIR;

  const dlCSV = (rows, fn) => { const a = document.createElement("a"); a.setAttribute("href", encodeURI("data:text/csv;charset=utf-8," + rows.map((r) => r.join(",")).join("\n"))); a.setAttribute("download", fn); document.body.appendChild(a); a.click(); document.body.removeChild(a); };
  const exportSummaryCSV = () => dlCSV([["Company", "ID", "Total Disbursed", "Disbursement Fees", "Withdrawal Fees", "Royalties", "Overdraft Interest Repay", "Total Revenue"], ...filteredSummary.map((r) => ['"' + (r.companyName || "").replace(/"/g, '""') + '"', r.companyId, r.totalDisbursed.toFixed(2), r.disbursementFees.toFixed(2), r.withdrawalFees.toFixed(2), r.totalRoyalties.toFixed(2), (r.overdraftInterestRepay || 0).toFixed(2), r.totalRevenue.toFixed(2)])], "Revenue_Summary_" + new Date().toISOString().slice(0, 10) + ".csv");
  const exportLedgerCSV = () => dlCSV([["Date", "Company", "Fee Type", "Tx Type", "Amount (KES)", "Description"], ...filteredLedger.map((e) => [toDateTimeStr(e.createdDate), '"' + (e.companyName || "").replace(/"/g, '""') + '"', FEE_TYPES[e.feeCategory]?.label || e.feeCategory, e.txType || "", Math.abs(Number(e.amount || 0)).toFixed(2), '"' + (e.description || "").replace(/"/g, '""') + '"'])], "Fee_Ledger_" + new Date().toISOString().slice(0, 10) + ".csv");
  const clearFilters = () => { setDateFrom(""); setDateTo(""); setLedgerFeeType("ALL"); setLedgerCompany("ALL"); setLedgerSearch(""); setLedgerPage(0); };

  const cardSx = { borderRadius: 2.5, backgroundColor: isDark ? colors.primary[400] : "#ffffff", border: "1px solid " + (isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0") };

  const KPICard = ({ title, value, subtitle, icon, color }) => (
    <Card sx={{ ...cardSx, height: "100%" }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400], letterSpacing: "0.5px" }}>{title}</Typography>
          {icon}
        </Box>
        {loading ? <CircularProgress size={24} /> : <Typography variant="h4" sx={{ fontWeight: 800, color }}>{formatKES(value)}</Typography>}
        {subtitle && <Typography variant="caption" sx={{ color: colors.grey[400] }}>{subtitle}</Typography>}
      </CardContent>
    </Card>
  );

  const FeeTypePill = ({ type }) => {
    const cfg = FEE_TYPES[type] || {};
    return <Chip label={cfg.label || type} size="small" sx={{ fontWeight: 700, fontSize: "0.72rem", backgroundColor: (cfg.color || "#666") + "22", color: cfg.color || "#666", border: "1px solid " + (cfg.color || "#666") + "55" }} />;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}><KPICard title="DISBURSEMENT FEES" value={totalDisbursementFees} subtitle="Platform processing charges" icon={<AccountBalanceOutlined sx={{ color: "#1766a2", fontSize: 20 }} />} color="#1766a2" /></Grid>
        <Grid item xs={12} sm={6} md={2.4}><KPICard title="WITHDRAWAL FEES" value={totalWithdrawalFees} subtitle="Sababisha withdrawal charges" icon={<CreditCardOutlined sx={{ color: "#22bf72", fontSize: 20 }} />} color="#22bf72" /></Grid>
        <Grid item xs={12} sm={6} md={2.4}><KPICard title="ROYALTIES" value={totalRoyalties} subtitle="Royalties earned from tenants" icon={<PaymentsOutlined sx={{ color: "#ed6c02", fontSize: 20 }} />} color="#ed6c02" /></Grid>
        <Grid item xs={12} sm={6} md={2.4}><KPICard title="OVERDRAFT INTEREST REPAY" value={totalOverdraftInterestRepay} subtitle="Overdraft interest collected" icon={<MonetizationOnOutlined sx={{ color: "#d32f2f", fontSize: 20 }} />} color="#d32f2f" /></Grid>
        <Grid item xs={12} sm={6} md={2.4}><KPICard title="GRAND TOTAL REVENUE" value={grandTotal} subtitle={"Across " + activeDisbursersCount + " transacting tenant" + (activeDisbursersCount !== 1 ? "s" : "") + " (" + revenueData.length + " registered)"} icon={<TrendingUpOutlined sx={{ color: "#9c27b0", fontSize: 20 }} />} color="#9c27b0" /></Grid>
      </Grid>

      <Card sx={cardSx}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, pt: 1 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.9rem" } }}>
            <Tab icon={<ReceiptLongOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label="Summary by Tenant" />
            <Tab icon={<FilterListOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label="Fee Ledger (Detailed)" />
          </Tabs>
        </Box>
        <CardContent sx={{ p: 3 }}>

          {activeTab === 0 && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>Revenue Breakdown by MFI Tenant</Typography>
                  <Chip label={filteredSummary.length + " Tenants"} size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: "0.75rem", height: 24 }} />
                </Box>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                  <FormControlLabel control={<Switch checked={activeOnly} onChange={(e) => { setActiveOnly(e.target.checked); setSummaryPage(0); }} size="small" color="primary" />} label={<Typography variant="body2" sx={{ fontSize: "0.82rem", fontWeight: 600, color: colors.grey[200] }}>Active Only</Typography>} sx={{ mr: 0.5 }} />
                  <TextField placeholder="Search tenant..." size="small" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setSummaryPage(0); }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 18, color: colors.grey[400] }} /></InputAdornment> }} sx={{ width: 220, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                  <Tooltip title="Refresh"><IconButton size="small" onClick={fetchRevenue} disabled={loading}>{loading ? <CircularProgress size={16} /> : <RefreshOutlined sx={{ fontSize: 18 }} />}</IconButton></Tooltip>
                  <Button size="small" startIcon={<DownloadOutlined />} onClick={exportSummaryCSV} sx={{ textTransform: "none", fontWeight: 600 }}>Export CSV</Button>
                </Stack>
              </Box>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
              ) : filteredSummary.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6 }}><Typography variant="body2" sx={{ color: colors.grey[400] }}>{searchQuery ? 'No MFI tenants match "' + searchQuery + '".' : "No active disbursements or revenue found. Uncheck 'Active Only' to view all."}</Typography></Box>
              ) : (
                <>
                  <TableContainer><Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: isDark ? colors.primary[500] : "#f8fafc" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Total Disbursed</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#1766a2" }}>Disbursement Fees</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#22bf72" }}>Withdrawal Fees</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#ed6c02" }}>Royalties</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#d32f2f" }}>Overdraft Interest Repay</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#9c27b0" }}>Total Revenue</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedSummary.map((row) => (
                        <TableRow key={row.companyId} hover>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{row.companyName}</Typography><Typography variant="caption" sx={{ color: colors.grey[400] }}>{row.disbursementCount > 0 ? row.disbursementCount + " disbursements" : "No disbursements yet"}</Typography></TableCell>
                          <TableCell align="right">{formatKES(row.totalDisbursed)}</TableCell>
                          <TableCell align="right" sx={{ color: "#1766a2", fontWeight: 600 }}>{formatKES(row.disbursementFees)}</TableCell>
                          <TableCell align="right" sx={{ color: "#22bf72", fontWeight: 600 }}>{formatKES(row.withdrawalFees)}</TableCell>
                          <TableCell align="right" sx={{ color: "#ed6c02", fontWeight: 600 }}>{formatKES(row.totalRoyalties)}</TableCell>
                          <TableCell align="right" sx={{ color: "#d32f2f", fontWeight: 600 }}>{formatKES(row.overdraftInterestRepay || 0)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, color: "#9c27b0" }}>{formatKES(row.totalRevenue)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f1f5f9" }}>
                        <TableCell sx={{ fontWeight: 800 }}>TOTALS</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>{formatKES(revenueData.reduce((s, r) => s + r.totalDisbursed, 0))}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: "#1766a2" }}>{formatKES(totalDisbursementFees)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: "#22bf72" }}>{formatKES(totalWithdrawalFees)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: "#ed6c02" }}>{formatKES(totalRoyalties)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: "#d32f2f" }}>{formatKES(totalOverdraftInterestRepay)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: "#9c27b0" }}>{formatKES(grandTotal)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table></TableContainer>
                  <TablePagination component="div" count={filteredSummary.length} page={summaryPage} onPageChange={(_, p) => setSummaryPage(p)} rowsPerPage={summaryRowsPerPage} onRowsPerPageChange={(e) => { setSummaryRowsPerPage(parseInt(e.target.value, 10)); setSummaryPage(0); }} rowsPerPageOptions={[5, 10, 25, 50, 100]} sx={{ borderTop: "1px solid " + (isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0") }} />
                </>
              )}
            </>
          )}

          {activeTab === 1 && (
            <>
              <Box sx={{ p: 2, mb: 2.5, borderRadius: 2, backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc", border: "1px solid " + (isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0") }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <FilterListOutlined sx={{ fontSize: 18, color: colors.grey[300] }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.grey[200] }}>Filter Ledger</Typography>
                  <Button size="small" startIcon={<ClearOutlined />} onClick={clearFilters} sx={{ ml: "auto", textTransform: "none", fontSize: "0.78rem", color: colors.grey[400] }}>Clear filters</Button>
                </Box>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={2}><TextField label="From Date" type="date" size="small" fullWidth value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setLedgerPage(0); }} InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayOutlined sx={{ fontSize: 16, color: colors.grey[400] }} /></InputAdornment> }} /></Grid>
                  <Grid item xs={12} sm={6} md={2}><TextField label="To Date" type="date" size="small" fullWidth value={dateTo} onChange={(e) => { setDateTo(e.target.value); setLedgerPage(0); }} InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayOutlined sx={{ fontSize: 16, color: colors.grey[400] }} /></InputAdornment> }} /></Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Fee Type</InputLabel>
                      <Select value={ledgerFeeType} onChange={(e) => { setLedgerFeeType(e.target.value); setLedgerPage(0); }} label="Fee Type">
                        <MenuItem value="ALL">All Fee Types</MenuItem>
                        <MenuItem value="DISBURSEMENT_FEE">Disbursement Fee</MenuItem>
                        <MenuItem value="WITHDRAWAL_FEE">Withdrawal Fee</MenuItem>
                        <MenuItem value="ROYALTY">Royalty</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Company</InputLabel>
                      <Select value={ledgerCompany} onChange={(e) => { setLedgerCompany(e.target.value); setLedgerPage(0); }} label="Company">
                        <MenuItem value="ALL">All Companies</MenuItem>
                        {companyList.map((c) => { const cid = c.Id ?? c.id; return <MenuItem key={cid} value={String(cid)}>{c.CompanyName ?? c.companyName ?? c.Name ?? ("Tenant #" + cid)}</MenuItem>; })}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}><TextField placeholder="Search description, type..." size="small" fullWidth value={ledgerSearch} onChange={(e) => { setLedgerSearch(e.target.value); setLedgerPage(0); }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 18, color: colors.grey[400] }} /></InputAdornment> }} /></Grid>
                </Grid>
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                {[{ label: "Disbursement Fees", value: lTotalDisb, color: "#1766a2" }, { label: "Withdrawal Fees", value: lTotalWith, color: "#22bf72" }, { label: "Royalties", value: lTotalRoy, color: "#ed6c02" }, { label: "Total (Filtered)", value: lGrand, color: "#9c27b0" }].map((k) => (
                  <Grid item xs={6} md={3} key={k.label}>
                    <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: k.color + "14", border: "1px solid " + k.color + "33", textAlign: "center" }}>
                      <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, display: "block" }}>{k.label}</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: k.color }}>{formatKES(k.value)}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.grey[100] }}>Fee Ledger Entries</Typography>
                  <Chip label={filteredLedger.length + " records"} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: "0.72rem", height: 22 }} />
                </Box>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Refresh ledger"><IconButton size="small" onClick={fetchLedger} disabled={ledgerLoading}>{ledgerLoading ? <CircularProgress size={16} /> : <RefreshOutlined sx={{ fontSize: 18 }} />}</IconButton></Tooltip>
                  <Button size="small" startIcon={<DownloadOutlined />} onClick={exportLedgerCSV} disabled={filteredLedger.length === 0} sx={{ textTransform: "none", fontWeight: 600 }}>Export CSV</Button>
                </Stack>
              </Box>

              {ledgerError && <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>{ledgerError}</Alert>}

              {ledgerLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6, gap: 2 }}><CircularProgress /><Typography variant="body2" sx={{ color: colors.grey[400] }}>Loading fee ledger across all tenants...</Typography></Box>
              ) : filteredLedger.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6 }}><Typography variant="body2" sx={{ color: colors.grey[400] }}>No fee entries found for the selected filters.</Typography></Box>
              ) : (
                <>
                  <TableContainer><Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: isDark ? colors.primary[500] : "#f8fafc" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Fee Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Tx Type</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>GL Journal</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedLedger.map((e) => (
                        <TableRow key={e.id} hover>
                          <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>{toDateTimeStr(e.createdDate)}</TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{e.companyName}</Typography><Typography variant="caption" sx={{ color: colors.grey[400] }}>ID: {e.companyId}</Typography></TableCell>
                          <TableCell><FeeTypePill type={e.feeCategory} /></TableCell>
                          <TableCell><Typography variant="caption" sx={{ fontWeight: 600, color: colors.grey[300] }}>{e.txType || "â€”"}</Typography></TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>{formatKES(Math.abs(Number(e.amount || 0)))}</TableCell>
                          <TableCell sx={{ maxWidth: 260 }}><Typography variant="caption" sx={{ color: colors.grey[300], display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.description || "â€”"}</Typography></TableCell>
                          <TableCell><Typography variant="caption" sx={{ color: colors.grey[400] }}>{e.glJournalId ? "JE-" + e.glJournalId : "â€”"}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table></TableContainer>
                  <TablePagination component="div" count={filteredLedger.length} page={ledgerPage} onPageChange={(_, p) => setLedgerPage(p)} rowsPerPage={ledgerRowsPerPage} onRowsPerPageChange={(e) => { setLedgerRowsPerPage(parseInt(e.target.value, 10)); setLedgerPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} sx={{ borderTop: "1px solid " + (isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0") }} />
                </>
              )}
            </>
          )}

        </CardContent>
      </Card>
    </Box>
  );
};

export default RevenueReport;
